import React from "react";
import { useNavigate } from "react-router-dom";
import { Vg2Shell } from "./Vg2Shell";
import {
  getOwnerSummary,
  postContinue,
  postFullReset,
  postRestartSameProperty,
} from "@/lib/vg2Workspace";

/**
 * Owner Workspace home — Phase 8A.
 * Read/orchestration layer. Property-centered, calm, app-like.
 */
export default function Workspace() {
  const navigate = useNavigate();
  const [summary, setSummary] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [confirm, setConfirm] = React.useState(null); // {kind, payload}

  const load = React.useCallback(async () => {
    setError(null);
    try {
      const s = await getOwnerSummary({});
      setSummary(s);
    } catch (e) { setError(e); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const doContinue = async () => {
    setBusy(true);
    try {
      const r = await postContinue({});
      if (r.state === "ACTIVE_JOURNEY_VALID" && r.continue_route) {
        navigate(r.continue_route);
      }
    } catch (e) { setError(e); }
    finally { setBusy(false); }
  };

  const doRestart = async (payload) => {
    setBusy(true);
    try {
      const opId = `restart-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const r = await postRestartSameProperty({
        ...payload, operationId: opId,
      });
      navigate(r.continue_route);
    } catch (e) { setError(e); }
    finally { setBusy(false); setConfirm(null); }
  };

  const doFullReset = async () => {
    setBusy(true);
    try {
      const opId = `reset-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      await postFullReset({ operationId: opId });
      await load();
      navigate("/vg2");
    } catch (e) { setError(e); }
    finally { setBusy(false); setConfirm(null); }
  };

  return (
    <Vg2Shell>
      <main className="max-w-4xl mx-auto px-4 py-8" data-testid="vg2-workspace-root">
        <header className="mb-8">
          <p className="text-xs uppercase tracking-[0.14em] text-vg2-red mb-1">
            Workspace
          </p>
          <h1 className="font-display text-2xl md:text-3xl font-medium">
            Il tuo percorso ValutaGenio
          </h1>
        </header>

        {error && <ErrorPanel error={error} onRetry={load} />}
        {!summary && !error && (
          <p className="text-sm text-vg2-muted" data-testid="workspace-loading">
            Caricamento…
          </p>
        )}

        {summary && (
          <>
            <ActiveJourneySection
              view={summary.active_journey}
              onContinue={doContinue}
              onNewAnalysis={() => navigate("/vg2")}
              busy={busy} />

            <PropertiesSection
              properties={summary.properties}
              onOpenCase={(cid) => navigate(`/vg2/workspace/cases/${cid}`)}
              onRestartHere={(prop) => setConfirm({
                kind: "restart",
                payload: {
                  sourcePropertyId: prop.property_id,
                  objective: prop.latest_objective_summary || "Nuova analisi",
                  scenarioType: "sale",
                  valuationMode: "sale",
                },
                propLabel: prop.display_label,
              })} />

            <RecentAnalysesSection
              analyses={summary.recent_analyses}
              onOpen={(cid) => navigate(`/vg2/workspace/cases/${cid}`)} />

            <FooterActions
              onFullReset={() => setConfirm({ kind: "full_reset" })}
              onNewAnalysis={() => navigate("/vg2")} />

            <p className="text-xs text-vg2-muted mt-8" data-testid="workspace-auth-hint">
              La cronologia è legata a questo dispositivo.
              L'accesso con account per ritrovarla su altri dispositivi
              sarà disponibile a breve.
            </p>
          </>
        )}

        {confirm?.kind === "restart" && (
          <ConfirmDialog
            title="Nuova analisi per lo stesso immobile"
            body={
              <>
                Verrà avviato un percorso completamente nuovo per
                <strong className="mx-1">{confirm.propLabel}</strong>.
                Le analisi precedenti restano nella cronologia. Nessun
                dato analitico verrà riutilizzato.
              </>
            }
            confirmLabel="Avvia nuova analisi"
            onConfirm={() => doRestart(confirm.payload)}
            onCancel={() => setConfirm(null)}
            busy={busy}
            testid="workspace-confirm-restart" />
        )}

        {confirm?.kind === "full_reset" && (
          <ConfirmDialog
            title="Azzera il percorso attivo"
            body={
              <>Il percorso attualmente in corso viene chiuso.
              La cronologia resta consultabile. Puoi ricominciare da zero
              quando vuoi.</>
            }
            confirmLabel="Azzera"
            onConfirm={doFullReset}
            onCancel={() => setConfirm(null)}
            busy={busy}
            testid="workspace-confirm-reset" />
        )}
      </main>
    </Vg2Shell>
  );
}


function ActiveJourneySection({ view, onContinue, onNewAnalysis, busy }) {
  if (view.state === "NO_ACTIVE_JOURNEY") {
    return (
      <section className="mb-8 border border-vg2-line rounded p-5"
               data-testid="workspace-active-none">
        <p className="font-display text-lg font-medium mb-2">
          Nessun percorso in corso
        </p>
        <p className="text-sm text-vg2-muted mb-4">
          Non c'è un'analisi attiva al momento. Puoi iniziarne una nuova
          o riaprire un'analisi passata.
        </p>
        <button onClick={onNewAnalysis} disabled={busy}
                data-testid="workspace-start-new-btn"
                className="px-4 py-2 bg-vg2-red text-white rounded hover:opacity-90">
          Inizia una nuova analisi
        </button>
      </section>
    );
  }
  if (view.state === "ACTIVE_CONTEXT_INVALID") {
    return (
      <section className="mb-8 border border-vg2-line rounded p-5"
               data-testid="workspace-active-invalid">
        <p className="font-display text-lg font-medium mb-2">
          Percorso non più disponibile
        </p>
        <p className="text-sm text-vg2-muted mb-4">
          Il percorso attivo non è più raggiungibile. Puoi ricominciare
          da zero — la cronologia resta consultabile.
        </p>
        <button onClick={onNewAnalysis} disabled={busy}
                data-testid="workspace-start-new-btn"
                className="px-4 py-2 bg-vg2-red text-white rounded hover:opacity-90">
          Inizia una nuova analisi
        </button>
      </section>
    );
  }
  return (
    <section className="mb-8 border-l-4 border-vg2-red bg-vg2-subtle p-5 rounded-r"
             data-testid="workspace-active-current">
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-red mb-1">
        Percorso in corso
      </p>
      <p className="font-display text-lg font-medium mb-1"
         data-testid="workspace-active-property">
        {view.property_display_label}
      </p>
      <p className="text-sm text-vg2-muted mb-2">
        Obiettivo: {view.objective_summary} · {view.current_stage_label}
      </p>
      {view.selected_direction_label && (
        <p className="text-sm text-vg2-ink mb-3">
          Direzione scelta: {view.selected_direction_label}
        </p>
      )}
      {view.eva_stale_marker && (
        <p className="text-xs text-vg2-red mb-3"
           data-testid="workspace-eva-stale-marker">
          {view.eva_stale_marker}
        </p>
      )}
      <button onClick={onContinue} disabled={busy}
              data-testid="workspace-continue-btn"
              className="px-4 py-2 bg-vg2-red text-white rounded hover:opacity-90">
        {view.next_action_label || "Continua"}
      </button>
    </section>
  );
}


function PropertiesSection({ properties, onOpenCase, onRestartHere }) {
  if (!properties?.length) return null;
  return (
    <section className="mb-8" data-testid="workspace-properties">
      <h2 className="font-display text-lg font-medium mb-3">I tuoi immobili</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {properties.map((p) => (
          <div key={p.property_id}
               className="border border-vg2-line rounded p-4"
               data-testid={`workspace-property-${p.property_id}`}>
            <p className="font-display font-medium mb-1">{p.display_label}</p>
            <p className="text-xs text-vg2-muted mb-2">
              {p.type_label} · {p.analysis_count}
              {p.analysis_count === 1 ? " analisi" : " analisi"}
            </p>
            {p.active_journey_here && (
              <p className="text-xs text-vg2-red mb-2"
                 data-testid={`workspace-property-active-${p.property_id}`}>
                Percorso in corso qui
              </p>
            )}
            <button onClick={() => onRestartHere(p)}
                    className="text-sm underline hover:text-vg2-ink"
                    data-testid={`workspace-property-restart-${p.property_id}`}>
              Nuova analisi per questo immobile
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}


function RecentAnalysesSection({ analyses, onOpen }) {
  if (!analyses?.length) return null;
  return (
    <section className="mb-8" data-testid="workspace-recent">
      <h2 className="font-display text-lg font-medium mb-3">Analisi recenti</h2>
      <ul className="divide-y divide-vg2-line border border-vg2-line rounded">
        {analyses.map((a) => (
          <li key={a.case_id} className="p-3 flex items-center justify-between"
              data-testid={`workspace-analysis-${a.case_id}`}>
            <div className="pr-3">
              <p className="text-sm font-medium">
                {a.objective_summary}
                {a.is_current && (
                  <span className="ml-2 text-xs text-vg2-red"
                        data-testid={`workspace-analysis-current-${a.case_id}`}>
                    · corrente
                  </span>
                )}
              </p>
              <p className="text-xs text-vg2-muted">
                {new Date(a.created_at).toLocaleDateString("it-IT")} · {a.journey_stage_label}
                {a.selected_direction_label && ` · ${a.selected_direction_label}`}
              </p>
            </div>
            <button onClick={() => onOpen(a.case_id)}
                    className="text-sm underline hover:text-vg2-ink whitespace-nowrap"
                    data-testid={`workspace-analysis-open-${a.case_id}`}>
              Apri
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}


function FooterActions({ onFullReset, onNewAnalysis }) {
  return (
    <section className="mt-4 pt-4 border-t border-vg2-line flex flex-wrap gap-3"
             data-testid="workspace-footer-actions">
      <button onClick={onNewAnalysis}
              className="px-4 py-2 border border-vg2-red text-vg2-red rounded hover:bg-vg2-red hover:text-white"
              data-testid="workspace-new-analysis-btn">
        Nuova analisi da zero
      </button>
      <button onClick={onFullReset}
              className="px-4 py-2 border border-vg2-line rounded hover:bg-vg2-subtle"
              data-testid="workspace-full-reset-btn">
        Azzera il percorso attivo
      </button>
    </section>
  );
}


function ConfirmDialog({ title, body, confirmLabel, onConfirm, onCancel, busy, testid }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
         role="dialog" aria-modal="true" data-testid={testid}>
      <div className="bg-white rounded max-w-md w-full p-5">
        <p className="font-display font-medium text-lg mb-3">{title}</p>
        <p className="text-sm text-vg2-ink mb-4">{body}</p>
        <div className="flex justify-end gap-3">
          <button onClick={onCancel} disabled={busy}
                  className="px-4 py-2 border border-vg2-line rounded hover:bg-vg2-subtle"
                  data-testid={`${testid}-cancel`}>
            Annulla
          </button>
          <button onClick={onConfirm} disabled={busy}
                  className="px-4 py-2 bg-vg2-red text-white rounded disabled:opacity-40"
                  data-testid={`${testid}-confirm`}>
            {busy ? "…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}


function ErrorPanel({ error, onRetry }) {
  return (
    <div className="border border-vg2-line rounded p-5" data-testid="workspace-error">
      <p className="text-sm text-vg2-ink mb-3">
        {error?.message || "Errore imprevisto."}
      </p>
      <button onClick={onRetry}
              className="px-4 py-2 border border-vg2-red text-vg2-red rounded hover:bg-vg2-red hover:text-white"
              data-testid="workspace-retry-btn">
        Riprova
      </button>
    </div>
  );
}
